import { Permission } from "pages/Role";
import { User } from "./UserAccountList";
import { useEffect, useState } from "react";
import { Table } from 'antd';
import type { TableColumnsType, TableProps } from 'antd';
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { useTheme } from "components/provider/theme";
import { API_URL } from "env";

interface DataType {
    key: React.Key;
    accountId: string;
    nickName: string;
    loginId: string;
    platform: string;
    broker: string;
}
const columns: TableColumnsType<DataType> = [
    {
        title: 'Account ID',
        dataIndex: 'accountId',
        key: 'accountId',
        render: (text) => <span className="">{text}</span>,
        sorter: (a, b) => a.accountId.localeCompare(b.accountId),
    },
    {
        title: 'NickName',
        dataIndex: 'nickName',
        key: 'nickName',
        render: (text) => <span className="">{text}</span>,
        sorter: (a, b) => a.nickName.localeCompare(b.nickName),
    },
    {
        title: 'Login ID',
        dataIndex: 'loginId',
        key: 'loginId',
        render: (text) => <span className="">{text}</span>,
        sorter: (a, b) => a.loginId.localeCompare(b.loginId),
    },
    {
        title: 'Platform',
        dataIndex: 'platform',
        key: 'platform',
        render: (text) => <span className="">{text}</span>,
        sorter: (a, b) => a.platform.localeCompare(b.platform),
    },
    {
        title: 'Broker',
        dataIndex: 'broker',
        key: 'broker',
        render: (text) => <span className="">{text}</span>,
        sorter: (a, b) => a.broker.localeCompare(b.broker),
    },
]

const UserDetailModalContent = (
    { user, onClose }: { user: User; onClose: () => void }
) => {
    const {theme} = useTheme();
    const [role, setRole] = useState<string[]>();
    const [userAccount, setUserAccount] = useState<DataType[] | null>(null);
    async function fetchData() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/user/${user.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            const roleData = data.data.roles.map((role: any) => role.display_name);
            setRole(roleData);
        } catch (error) {
            console.error('Error fetching role:', error);
        }
    }
    async function fetchUserAccount() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/trade_accounts?user_id=${user.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            const account = data.data.items.map((item: any) => ({
                key: item.id,
                accountId: item.id,
                nickName: item.nickname,
                loginId: item.account_number,
                platform: item.platform,
                broker: item.broker
            }));
            setUserAccount(account || null);
        } catch (error) {
            console.error('Error fetching user account:', error);
            return null;
        }
    }
    useEffect(() => {
        fetchUserAccount();
        fetchData();
    }, []);
    return (
        <div className="p-4 overflow-y-auto">
            <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                    {
                        user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.name} className="w-24 h-24 rounded-full" />
                        ) : (
                            <div className="flex-shrink-0 h-24 w-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-medium text-[36px]">
                                    {
                                        user.firstName && user.lastName
                                            ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
                                            : user.name ? user.name.charAt(0) : "U"
                                    }
                                </span>
                            </div>
                        )
                    }
                    <div className="flex-1 ml-4">
                        <h2 className={`text-xl font-semibold ${theme === 'dark' ? "": "text-gray-800"}`}>{user.name || "No Name Provided"}</h2>
                        <p className={`${theme === 'dark' ? "text-gray-300": "text-gray-600"} `}>{user.email || "No Email Provided"}</p>
                    </div>
                    <div className="w-[20%] float-right">
                        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? "": "text-gray-700"}`}>ID</label>
                        <p className={`mt-1 border border-gray-400 rounded-[8px] py-2 px-4 ${theme === 'dark' ? '' : 'text-gray-900' }`}>{user.id || "No value"}</p>
                    </div>
                </div>
                <div>
                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? "": "text-gray-700"}`}>Country</label>
                    <p className={`mt-1 border border-gray-400 rounded-[8px] py-2 px-4 ${theme === 'dark' ? '' : 'text-gray-900' }`}>{user != undefined && user.country ? (user.country) : "No value"}</p>
                </div>
                <div className="flex flex-row justify-between py-2">
                    <h2 className="font-medium text-[18px] !mb-0">Assign Accounts</h2>
                    <ArrowCounterClockwiseIcon
                        className={`cursor-pointer  ${theme === 'dark' ? 'text-gray-100 hover:text-gray-400' : ' text-gray-500 hover:text-gray-700' } `}
                        onClick={() => {
                            fetchUserAccount();
                            fetchData();
                        }}
                        size={18} />
                </div>
            </div>
            <Table<DataType>
                columns={columns}
                dataSource={userAccount || []}
                showSorterTooltip={{ target: 'sorter-icon' }}
            />

            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            </div>
            {
                role && role.length > 0 && role.map((roleName, index) => (
                    <div key={index}>
                        <p className="mt-1 text-gray-900 border border-gray-400 rounded-[8px] py-2 px-4">{roleName || "No value"}</p>
                    </div>
                ))
            }
        </div>
    );
}

export default UserDetailModalContent;